// The Take Off's left-hand blocks: the rates the option is priced at (the
// template's G2:N18) and the Repaint Summary. A rate typed here changes it for
// this option only (hig_scope.rates); the branch's or setting's value shows
// greyed in the box, and clearing the box goes back to it.
import type { ReactNode } from 'react';
import type { OptionScope, RateKey } from '@higgins/m2q-pricing';
import type { PricedOption, PricingRates } from '@higgins/m2q-pricing';
import { NumberField } from './number-field';
import { formatMoney, formatPercent, formatSell, formatTwo } from './surface-format';

type Half = 'sell' | 'cost';
type Changes = OptionScope['rates'];
type OnRate = (key: RateKey, half: Half, value: number | null) => void;

type Category = PricedOption['breakdown'][number]['category'];
const LABOUR: Category[] = ['labour', 'overtime', 'foreman', 'abseil', 'allowance'];
const MATERIAL: Category[] = ['paint', 'consumables'];
const ACCESS: Category[] = ['hire'];
const OTHER: Category[] = ['fuelLevy', 'otherTrades'];

export function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <h3 className="mb-2 font-semibold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

export function RateRow({ label, sell, cost }: { label: ReactNode; sell: ReactNode; cost?: ReactNode }) {
  return (
    <tr>
      <td className="whitespace-nowrap py-0.5 pr-2 text-slate-600">{label}</td>
      <td className="py-0.5 text-right tabular-nums">{sell}</td>
      <td className="py-0.5 pl-3 text-right tabular-nums text-slate-500">{cost ?? ''}</td>
    </tr>
  );
}

/** A rate box: this option's value if changed (yellow), else the base value as the placeholder. */
function RateBox({ rateKey, half, base, changes, onRate }: { rateKey: RateKey; half: Half; base: number; changes: Changes; onRate: OnRate }) {
  const value = changes?.[rateKey]?.[half];
  return (
    <NumberField
      compact
      value={value}
      placeholder={formatTwo(base)}
      onChange={(v) => onRate(rateKey, half, v)}
      label={`${rateKey} ${half}`}
      className={`w-16 ${value !== undefined ? 'bg-amber-50' : ''}`}
    />
  );
}

interface PanelProps {
  rates: PricingRates;
  /** The same rates without this option's changes: the branch's labour rate and the settings. */
  base: PricingRates;
  changes: Changes;
  onRate: OnRate;
  rateName: string;
  /** The option's paint list (paint-list.tsx), shown under Paint/Material. */
  paintList: ReactNode;
}

type SellCostKey = 'labour' | 'overtime' | 'foreman' | 'abseil' | 'allowance';

const LABOUR_ROWS: { key: Exclude<SellCostKey, 'labour'>; label: string }[] = [
  { key: 'overtime', label: 'Overtime' },
  { key: 'foreman', label: 'Foreman' },
  { key: 'abseil', label: 'Abseil' },
  { key: 'allowance', label: 'Allowance: per hr' },
];

export function RatesPanel({ rates, base, changes, onRate, rateName, paintList }: PanelProps) {
  const box = (key: RateKey, half: Half, value: number) => <RateBox rateKey={key} half={half} base={value} changes={changes} onRate={onRate} />;
  const sellCost = (key: SellCostKey) => ({ sell: box(key, 'sell', base[key].sell), cost: box(key, 'cost', base[key].cost) });
  return (
    <Block title="Labour Rates">
      <table className="w-full">
        <thead>
          <tr className="text-xs text-slate-500">
            <th />
            <th className="text-right font-normal">Sell $</th>
            <th className="text-right font-normal">Cost $</th>
          </tr>
        </thead>
        <tbody>
          <RateRow label={<>Default rate <span className="block text-xs font-medium text-slate-900">{rateName}</span></>} {...sellCost('labour')} />
          {LABOUR_ROWS.map((r) => (
            <RateRow key={r.key} label={r.label} {...sellCost(r.key)} />
          ))}
          <RateRow label="Wash: m²/hr" sell={box('wash', 'sell', base.washRate)} />
        </tbody>
      </table>
      <h4 className="mt-3 mb-1 text-xs text-slate-500">Paint/Material</h4>
      {paintList}
      <table className="mt-2 w-full">
        <tbody>
          <RateRow label="Mark-Up Material" sell={formatPercent(rates.markupMaterials)} />
          <RateRow label="Mark-Up Other" sell={formatPercent(rates.markupOther)} />
          <RateRow label="Consumables $/lt" sell={box('consumables', 'sell', base.consumablesPerLitre)} />
          <RateRow label="Fuel levy $/hr" sell={box('fuelLevy', 'sell', base.fuelLevyPerHour)} />
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-500">From CRM reference data at the pricing date. Type a rate to change it for this option only; clear it to go back.</p>
    </Block>
  );
}

/** `priceShown`: false while the REVIEW holds the sell price back — the total sell and the profit (sell less cost) show a dash. */
/** The template's summary blocks (Repaint, Overall): cost by category with its share, hours and litres, sell, GP and profit. */
export interface CostFigures {
  /** Labour, material, access and other cost, in the template's order. */
  costs: [labour: number, material: number, access: number, other: number];
  cost: number;
  sell: number;
  gp: number;
  hours: number;
  litres: number;
}

const COST_LABELS = ['Labour cost', 'Material cost', 'Access cost', 'Other cost'];

export function CostSummary({ title, hoursLabel, figures, priceShown }: { title: string; hoursLabel: string; figures: CostFigures; priceShown: boolean }) {
  const { costs, cost, sell, gp, hours, litres } = figures;
  const share = (value: number) => (cost > 0 ? formatPercent(value / cost) : '');
  return (
    <Block title={title}>
      <table className="w-full">
        <tbody>
          {costs.map((value, i) => (
            <RateRow key={COST_LABELS[i]} label={COST_LABELS[i]} sell={formatMoney(value)} cost={share(value)} />
          ))}
          <RateRow label={<strong>Total Cost</strong>} sell={formatMoney(cost)} />
          <RateRow label={hoursLabel} sell={formatTwo(hours)} cost={formatTwo(litres)} />
          <RateRow label={<strong>Total Sell</strong>} sell={formatSell(sell, priceShown)} />
          <RateRow label="GP% · Profit" sell={formatPercent(gp)} cost={formatSell(sell - cost, priceShown)} />
        </tbody>
      </table>
    </Block>
  );
}

export function RepaintSummary({ option, priceShown }: { option: PricedOption; priceShown: boolean }) {
  const costOf = (categories: Category[]) => option.breakdown.filter((b) => categories.includes(b.category)).reduce((sum, b) => sum + b.cost, 0);
  const costs: CostFigures['costs'] = [costOf(LABOUR), costOf(MATERIAL), costOf(ACCESS), costOf(OTHER)];
  return <CostSummary title="Repaint Summary" hoursLabel="Repaint Hrs · Lts" figures={{ ...option, costs }} priceShown={priceShown} />;
}
